import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Icon } from "@nexus/ui";
import type { LlmUsageTenantTotals } from "@/features/llm-usage/api/llm-usage.api";
import {
  AdminDataPanel,
  AdminDataTable,
  AdminSearchInput,
  type AdminColumn,
} from "@/features/admin/components/layout";
import type { SortValue } from "@/features/admin/lib/table-sort";
import { isLongTail, matchesQuery } from "../lib/table-filters";
import { LongTailChip } from "./LongTailChip";
import { TenantMark, TENANT_MARK_SIZE } from "./TenantMark";
import {
  tokenMeasureColumns,
  tokenMeasureSortValue,
  type TokenMeasureKey,
} from "./token-measure-columns";

interface LlmUsageTenantTableProps {
  rows: LlmUsageTenantTotals[];
  isLoading: boolean;
  /** Rows belong to the previous filter while the next one is in flight. */
  isStale: boolean;
  /** Window total, so each row can show its share of the bill. */
  totalTokens: number;
  /** `tokenUsageLongTailPercent`: the share a row needs to escape the fold. */
  longTailPercent: number;
  /**
   * Called with the tenant a row names. Omit to leave the rows inert. The page
   * decides what a second click on the current tenant means, so this only
   * reports which row was chosen.
   */
  onSelectTenant?: (tenantId: string) => void;
  /** The tenant the page is currently scoped to, marked as the active row. */
  selectedTenantId?: string | null;
  /**
   * The per-tenant spend level a row is marked against, or null when nothing
   * should be marked. `alertThresholdForWindow` decides that — a level only
   * means something when the alert is on and evaluates this same window.
   */
  spendAlertTokens?: number | null;
}

export type TenantSortKey = TokenMeasureKey | "tenant";

/** Spend, matching the order the server already ranked the rows in. */
const DEFAULT_SORT = { key: "totalTokens", dir: "desc" } as const;

/**
 * Three cases, and conflating them would misreport the bill: work no tenant
 * asked for, a tenant whose row is gone but whose id still traces, and an
 * ordinary named tenant. Sorting and searching both run on this label, so the
 * two rows without a name answer to what is actually rendered.
 */
function tenantLabel(row: LlmUsageTenantTotals, t: TFunction): string {
  if (row.tenantId == null) return t("admin.llmUsage.tenantTable.platform", "Platform");
  return row.tenantName ?? row.tenantId;
}

/**
 * Spend per tenant, ordered highest first until the reader says otherwise — the
 * question this answers is which orgs the bill belongs to, so the heaviest row
 * is the first one read.
 */
export function LlmUsageTenantTable({
  rows,
  isLoading,
  isStale,
  totalTokens,
  longTailPercent,
  onSelectTenant,
  selectedTenantId = null,
  spendAlertTokens = null,
}: LlmUsageTenantTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [search, setSearch] = useState("");
  const [hideLongTail, setHideLongTail] = useState(false);

  const searched = useMemo(
    () => rows.filter((row) => matchesQuery(tenantLabel(row, t), search)),
    [rows, search, t],
  );
  const longTailCount = useMemo(
    () =>
      searched.filter((row) => isLongTail(row.totalTokens, totalTokens, longTailPercent)).length,
    [searched, totalTokens, longTailPercent],
  );
  const visibleRows = useMemo(
    () =>
      hideLongTail
        ? searched.filter((row) => !isLongTail(row.totalTokens, totalTokens, longTailPercent))
        : searched,
    [searched, hideLongTail, totalTokens, longTailPercent],
  );

  const columns: AdminColumn<LlmUsageTenantTotals, TenantSortKey>[] = [
    {
      id: "tenant",
      header: t("admin.llmUsage.tenantTable.tenant", "Tenant"),
      isFill: true,
      isSortable: true,
      cell: (row) => <TenantLabel row={row} />,
    },
    ...tokenMeasureColumns<LlmUsageTenantTotals>({
      t,
      locale,
      totalTokens,
      spendAlertTokens,
      unpricedTokensOf: (row) => row.unpricedTokens,
    }),
  ];

  const sortValue = (row: LlmUsageTenantTotals, key: TenantSortKey): SortValue =>
    key === "tenant" ? tenantLabel(row, t) : tokenMeasureSortValue(row, key);

  return (
    <AdminDataPanel
      title={t("admin.llmUsage.tenantTable.title", "Tenants")}
      maxRows={8}
      controls={
        <>
          <AdminSearchInput
            value={search}
            onChange={setSearch}
            className="sm:w-52"
            placeholder={t("admin.llmUsage.tenantTable.searchPlaceholder", "Find a tenant")}
            label={t("admin.llmUsage.tenantTable.searchLabel", "Filter tenants by name")}
          />
          <LongTailChip
            active={hideLongTail}
            onToggle={() => setHideLongTail((prev) => !prev)}
            hiddenCount={longTailCount}
            thresholdPercent={longTailPercent}
          />
        </>
      }
    >
      <AdminDataTable
        caption={t("admin.llmUsage.tenantTable.caption", "Token usage per tenant")}
        columns={columns}
        rows={visibleRows}
        getRowId={(row) => row.tenantId ?? "__no_tenant__"}
        isLoading={isLoading}
        isStale={isStale}
        isFiltered={visibleRows.length < rows.length}
        emptyMessage={t("admin.llmUsage.table.empty", "No LLM calls in this window")}
        noMatchMessage={t("admin.llmUsage.tenantTable.noMatches", "No tenants match these filters")}
        defaultSort={DEFAULT_SORT}
        sortValue={sortValue}
        rowSelect={
          onSelectTenant
            ? {
                onSelect: (row) => row.tenantId != null && onSelectTenant(row.tenantId),
                isSelected: (row) => row.tenantId != null && row.tenantId === selectedTenantId,
                // Platform work is not something the page can scope to, so its
                // row names no action and stays inert.
                getLabel: (row, isSelected) =>
                  row.tenantId == null
                    ? null
                    : isSelected
                      ? t("admin.llmUsage.tenantTable.clearFilter", "Stop filtering by {{name}}", {
                          name: tenantLabel(row, t),
                        })
                      : t("admin.llmUsage.tenantTable.filterBy", "Show only {{name}}", {
                          name: tenantLabel(row, t),
                        }),
              }
            : undefined
        }
      />
    </AdminDataPanel>
  );
}

function TenantLabel({ row }: { row: LlmUsageTenantTotals }) {
  const { t } = useTranslation();

  if (row.tenantId == null) {
    return (
      <span className="flex items-center gap-2">
        {/* The shield stands where a logo would and holds a logo's width, so
            the platform row's name starts on the same pixel as every other
            row's in the one column the eye scans straight down. */}
        <span
          aria-hidden
          className="flex shrink-0 items-center justify-center"
          style={{ width: TENANT_MARK_SIZE, height: TENANT_MARK_SIZE }}
        >
          <Icon name="shield" size="xs" className="text-muted-foreground" />
        </span>
        <span className="text-muted-foreground">
          {t("admin.llmUsage.tenantTable.platform", "Platform")}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {t("admin.llmUsage.tenantTable.platformNote", "no tenant asked for this")}
        </span>
      </span>
    );
  }

  if (row.tenantName == null) {
    return (
      <span className="flex items-center gap-2">
        <TenantMark name={null} />
        <span className="font-mono text-[11px]">{row.tenantId}</span>
        <span className="text-[11px] text-muted-foreground">
          {t("admin.llmUsage.tenantTable.deleted", "deleted")}
        </span>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <TenantMark name={row.tenantName} logoUrl={row.logoUrl} brandColor={row.brandColor} />
      <span className="truncate">{row.tenantName}</span>
    </span>
  );
}
